import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '~/routes/query-params';

import { useTabsNavigation } from './tabs/hooks/useTabsNavigation';
import { ParentCategoryTab } from './tabs/parent-category-tab/ParentCategoryTab';
import { ThemeExposureTab } from './tabs/theme-exposure-tab/ThemeExposureTab';
import { ThemeOverviewTab } from './tabs/theme-overview-tab/ThemeOverviewTab';
import * as S from './ThemeWidget.styles';

export enum SubTabsType {
  ThemeExposure = 'themeExposure',
  ThemeOverview = 'themeOverview',
  ParentCategory = 'parentCategory',
  CompetitorOverview = 'competitorOverview',
  CompetitorDetail = 'competitorDetail',
}

export interface ThemeWidgetProps {
  entity: Entity;
  themeId: string;
}

export const ThemeWidget = ({ entity, themeId }: ThemeWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();

  const { data, isLoading, isError, error } = useCompanyTheme({
    entityTag: entity.tag,
  });
  const theme = data?.find(t => t.theme.id === themeId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <S.LoaderWrapper data-testid="theme-loader">
          <S.LoadingSkeletonLarge />
          <S.LoadingSkeletonMedium />
          <S.LoadingSkeletonSmall />
        </S.LoaderWrapper>
      );
    }

    if (isError || !theme) {
      throw new Error(error?.message);
    }

    const commonProps = {
      entity,
      theme,
    };

    switch (params[queryKeys.activeDrawerTab]) {
      case SubTabsType.ThemeOverview:
        return (
          <ThemeOverviewTab
            {...commonProps}
            title={t(
              'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeOverview.overview'
            )}
          />
        );
      case SubTabsType.ParentCategory:
        return <ParentCategoryTab {...commonProps} />;
      default:
        return <ThemeExposureTab {...commonProps} />;
    }
  };

  const subTabs = [
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.title'
      ),
      key: SubTabsType.ThemeExposure,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeOverview.title'
      ),
      key: SubTabsType.ThemeOverview,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.parentCategory.title'
      ),
      key: SubTabsType.ParentCategory,
    },
  ];
  const { activeIndex, onTabChange } = useTabsNavigation(
    subTabs,
    SubTabsType.ThemeExposure
  );

  if (!themeId) {
    return null;
  }

  return (
    <S.Container>
      <S.StyledButtonGroup
        labels={subTabs.map(t => t.label)}
        activeIndex={activeIndex}
        onClick={onTabChange}
      />
      <ErrorBoundaryWrapper>{renderContent()}</ErrorBoundaryWrapper>
    </S.Container>
  );
};
